import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, Send, Lock, ImageIcon, AlertCircle, Sparkles, Check, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTS = [
  { v: "open", label: "Open" },
  { v: "in_progress", label: "In progress" },
  { v: "resolved", label: "Resolved" },
  { v: "closed", label: "Closed" },
];

const formatDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const AdminTicketDetailPage = () => {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");
  const [updating, setUpdating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/support/tickets/${ticketId}`);
      setTicket(r.data);
    } catch (err) {
      toast.error("Could not load ticket", { description: err?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [ticketId]); // eslint-disable-line

  const onReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/messages`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (err) {
      toast.error("Could not send reply", { description: err?.response?.data?.detail });
    } finally {
      setSending(false);
    }
  };

  const onUpdate = async (changes) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/support/tickets/${ticketId}`, changes);
      if (changes.internal_note) setNote("");
      toast.success("Ticket updated");
      await load();
    } catch (err) {
      toast.error("Could not update ticket", { description: err?.response?.data?.detail });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!ticket) return <p>Ticket not found.</p>;

  return (
    <div className="space-y-6" data-testid="admin-ticket-detail">
      <Link to="/admin/support" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to inbox
      </Link>

      {/* Header */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{ticket.category}</p>
            <h1 className="text-2xl font-bold tracking-tight mt-1">{ticket.subject}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              From <strong className="text-foreground">{ticket.user_name}</strong> · {ticket.user_email}
            </p>
            {ticket.page_url && (
              <p className="text-xs text-muted-foreground mt-1">
                Page: <span className="font-mono">{ticket.page_url}</span>
              </p>
            )}
          </div>
          <select
            value={ticket.status}
            onChange={(e) => onUpdate({ status: e.target.value })}
            disabled={updating}
            data-testid="ticket-status-select"
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
          >
            {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Thread */}
      <div className="space-y-3" data-testid="ticket-thread">
        {ticket.messages?.map((m) => (
          <div
            key={m.id}
            className={`rounded-xl border p-4 ${m.author_role === "admin" ? "border-primary/30 bg-primary/5 ml-8" : "border-border bg-card mr-8"}`}
            data-testid={`msg-${m.author_role}`}
          >
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <p className="text-sm font-semibold">{m.author_name} <span className="text-xs text-muted-foreground font-normal ml-1">({m.author_role})</span></p>
              <p className="text-xs text-muted-foreground">{formatDateTime(m.created_at)}</p>
            </div>
            <p className="text-sm whitespace-pre-wrap">{m.body}</p>
          </div>
        ))}

        {ticket.screenshot_url && (
          <div className="rounded-xl border border-border bg-card p-3" data-testid="ticket-screenshot">
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Attached screenshot</p>
            <img
              src={`${BACKEND_URL}${ticket.screenshot_url}`}
              alt="Ticket screenshot"
              className="max-h-96 rounded-md border border-border"
            />
          </div>
        )}
      </div>

      {/* AI Draft panel */}
      <AiDraftPanel ticket={ticket} onUseDraft={(text) => setReply(text)} onRegenerate={load} />

      {/* Reply box */}
      <form onSubmit={onReply} className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="admin-reply-form">
        <p className="text-sm font-semibold">Reply to {ticket.user_name}</p>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={4}
          maxLength={4000}
          placeholder="Type your reply…"
          required
          data-testid="admin-reply-input"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
        />
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={sending || !reply.trim()} data-testid="admin-reply-send" className="gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send reply
          </Button>
        </div>
      </form>

      {/* Internal notes (admin-only) */}
      <div className="rounded-2xl border border-dashed border-amber-500/40 bg-amber-500/5 p-4 space-y-3" data-testid="internal-notes">
        <p className="text-sm font-semibold flex items-center gap-1.5 text-amber-700"><Lock className="w-3.5 h-3.5" /> Internal notes (admin only — student can&apos;t see)</p>
        {(ticket.internal_notes || []).map((n) => (
          <div key={n.id} className="text-sm border-l-2 border-amber-400/40 pl-3 py-1">
            <p className="text-xs text-muted-foreground">{n.author_name} · {formatDateTime(n.created_at)}</p>
            <p className="whitespace-pre-wrap">{n.body}</p>
          </div>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); if (note.trim()) onUpdate({ internal_note: note.trim() }); }} className="flex gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add an internal note (just for the team)…"
            maxLength={2000}
            data-testid="internal-note-input"
            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <Button type="submit" variant="outline" size="sm" disabled={!note.trim() || updating} data-testid="internal-note-save">Save</Button>
        </form>
      </div>
    </div>
  );
};

export default AdminTicketDetailPage;

// ============== AI Draft Panel ==============
const AiDraftPanel = ({ ticket, onUseDraft, onRegenerate }) => {
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/support/tickets/${ticket.id}/ai-suggest`);
      toast.success("AI draft generated");
      onRegenerate();
    } catch (err) {
      toast.error("Could not generate draft", { description: err?.response?.data?.detail });
    } finally {
      setGenerating(false);
    }
  };

  const draft = ticket.ai_draft;
  const conf = ticket.ai_draft_confidence;
  const cat = ticket.ai_draft_category;
  const escalate = ticket.ai_draft_escalate;

  if (!draft) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-4 flex items-center justify-between gap-3" data-testid="ai-draft-empty">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <p className="text-sm">AI hasn&apos;t drafted a reply yet for this ticket.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={generate} disabled={generating} data-testid="ai-generate-draft-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          <span className="ml-1">Generate draft</span>
        </Button>
      </div>
    );
  }

  const confColor = conf >= 4 ? "text-primary" : conf >= 3 ? "text-amber-600" : "text-muted-foreground";

  return (
    <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5 space-y-3" data-testid="ai-draft-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <p className="text-sm font-semibold">AI suggested draft</p>
          <span className={`text-xs font-medium ${confColor}`}>Confidence {conf}/5 · {cat}</span>
          {escalate && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30">
              Recommends escalation
            </span>
          )}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={generate} disabled={generating} data-testid="ai-regenerate-btn" className="text-xs gap-1">
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Regenerate
        </Button>
      </div>

      <div className="text-sm whitespace-pre-wrap text-foreground/90 p-3 rounded-lg bg-background border border-border" data-testid="ai-draft-text">
        {draft}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={async () => {
          try {
            await axios.post(`${API}/support/tickets/${ticket.id}/messages`, { body: draft });
            toast.success("Sent");
            onRegenerate();
          } catch (err) {
            toast.error("Could not send", { description: err?.response?.data?.detail });
          }
        }} data-testid="ai-send-asis-btn" className="gap-1">
          <Check className="w-4 h-4" /> Send as-is
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => onUseDraft(draft)} data-testid="ai-edit-btn" className="gap-1">
          <Pencil className="w-4 h-4" /> Edit & send
        </Button>
      </div>
    </div>
  );
};
